import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Typography, Button, Divider } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { STATUS } from '../../utils/status';
import { useNavigate, useParams } from 'react-router-dom';
import { AddButton } from '../button/button';
import { TableCellHead } from '../table/tableCell';
import { useStyles } from '../../assets/styles/listTableStyle';
import DeleteModal from '../modal/deleteModal';
import Error from '../error/error';
import AddIcon from '@mui/icons-material/Add';
import SnackbarNotification from '../snackbar/snackbarNotification';
import TableRowItem from './reviewTableRowItem';
import { getReview, setUpdateReviewStatus, updateReview } from '../../redux/slices/reviewerSlice';
import { SearchBar } from '../inputField/textfield';
import { debounce } from 'lodash';
import ArrowButton from '../ArrowButton/arrowButton';
import { cloneProjectStages } from '../../redux/slices/projectSlice/addProjectSlice';
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import ProjectClonePopup from '../uploadTask/ProjectClonePopup';

const ReviewList = () => {
    const classes = useStyles();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id } = useParams();
    const [currentPage] = useState(1);
    const [itemsPerPage] = useState<number>(50);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userIdToDelete, setUserIdToDelete] = useState("");
    const [userDisable, setUserDisable] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [projectClonePopupOpen, setProjectClonePopupOpen] = useState(false);
    const [anchorE2, setAnchorE2] = useState(null);

    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const review = useSelector((state: RootState) => state.reviewStage.reviewer);
    const getStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);
    const updateMessage = useSelector((state: RootState) => state.reviewStage.updateReviewMessage);
    const updateStatus = useSelector((state: RootState) => state.reviewStage.updateReviewStatus);
    
    const cancelDelete = () => {
        setShowDeleteModal(false);
    };

    const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };
    const handleDeleteClick = (userId: string, disabled: boolean) => {
        setUserDisable(disabled)
        setUserIdToDelete(userId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setShowDeleteModal(false);
        try {
            await dispatch(updateReview({ id: userIdToDelete, project_id: id, is_disabled: userDisable ? false : true }));
            dispatch(
                getReview({ pageSize: itemsPerPage, project_id: id || '' })
            );
            setShowDeleteModal(false);
        } catch (error) {
            handleSnackbar(updateMessage, 'error');
        }
    };

    useEffect(() => {
        const handleUpdateStatus = () => {
            if (updateStatus === STATUS.SUCESS) {
                handleSnackbar(updateMessage, 'success');
                setTimeout(() => {
                    navigate(`/project/${id}?tab=review`);
                }, 1000);
            }

            if (updateStatus === STATUS.FAILED) {
                handleSnackbar(updateMessage, 'error');
            }
        };

        handleUpdateStatus();
        return () => {
            if (updateStatus === STATUS.SUCESS || updateStatus === STATUS.FAILED) {
                dispatch(setUpdateReviewStatus(STATUS.NOT_STARTED));
            }
        };
    }, [updateStatus, navigate, dispatch]);

    useEffect(() => {
        dispatch(getReview({ search: searchTerm, page: currentPage, pageSize: itemsPerPage, project_id: id || '' }))
    }, [dispatch, searchTerm, currentPage, itemsPerPage, id]);

    const handleAddClick = () => {
        navigate(`/addReviewer/${id}`)
    };

    const handleEditClick = (row: any) => {
        navigate(`/editReviewer/${id}/${row.id}`);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const debouncedResults = useMemo(() => {
        return debounce(handleSearchChange, 300);
    }, []);

    useEffect(() => {
        return () => {
            debouncedResults.cancel();
        };
    });

    useEffect(() => {
        const handleGetStatus = () => {
            if (getStatus === STATUS.PENDING) {
                setLoading(true)
            }
            if (getStatus === STATUS.SUCESS) {
                setLoading(false)
            }
            if (getStatus === STATUS.FAILED) {
                setLoading(false)
            }
        };

        handleGetStatus();
    }, [getStatus])

    const headers = ["STAGE NAME", "CATEGORY", "CONFIGURATION TYPE", "CREATED AT", "LAST UPDATED TIME"];


    const handleMenuClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleReviewStagesClone = (selectedProjectId:any) => {
        const payload = {
            target_project_id: id,
            source_project_id: selectedProjectId?.source_project_id,
            type: "review_stages",
        };
        dispatch(cloneProjectStages(payload));
    }

    const handleProjectCloneOpen = () => {
        setProjectClonePopupOpen(true);
        setAnchorE2(null);
        setAnchorEl(null);
    };
    const handleProjectCloneClose = () => setProjectClonePopupOpen(false);


    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <Typography sx={{ marginLeft: '10px', fontFamily: 'Montserrat', fontWeight: 700, fontSize: '20px', lineHeight: '14px', marginTop: '20px' }}>Stages</Typography>
                    <Divider sx={{ marginTop: '10px', width: '115%' }} />
                </div>


                <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleAddClick}
                        sx={{
                            width: 'auto',
                            height: '40px',
                            marginTop: '10px',
                             borderRadius: '8 0 0 8',
                            padding: '8px, 16px, 8px, 16px',
                            backgroundColor: '#3D85C6',
                            textTransform: 'none',
                            marginLeft: '10px',
                            '&:hover': {
                                backgroundColor: '#3D85C6',
                            },
                        }}>
                        <PreviewOutlinedIcon fontSize='small' sx={{ marginRight: '10px' }} /> Add Stage
                    </Button>
                    {/* <ArrowButton
                        handleMenuClick={handleMenuClick}
                        anchorEl={anchorEl}
                        handleMenuClose={handleMenuClose}
                        handleAddClick={handleProjectCloneOpen}
                        menuText={"Clone Stages"}
                    /> */}
                </div> 
            </div>
            <TableContainer>
                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                        <CircularProgress />
                    </div>
                )}
                {!loading && (
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead sx={{ backgroundColor: '#F7F9FA', '&:first-child td, &:first-child th': { borderBottom: 0 } }}>
                            <TableRow>
                                {headers.map((header) => (
                                    <TableCellHead key={header}>{header}</TableCellHead>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getStatus === STATUS.SUCESS && review && review.results && review.results.length > 0 ? (
                                review.results.map((row: any) => (
                                    <TableRowItem
                                        key={row.id}
                                        row={row}
                                        onEditClick={handleEditClick}
                                        onDeleteClick={handleDeleteClick}
                                    />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} className={classes.noUsersMessage}>
                                        <Error message={'No stage found. Click Add Stages to create one.'} />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <DeleteModal
                open={showDeleteModal}
                onCancel={cancelDelete}
                onConfirm={confirmDelete}
                deleteText={userDisable ? 'Enable' : 'Disable'}
                reviewListText={<>This review stage will marked as {userDisable ? 'active' : 'inactive'}. <br /> You can {userDisable ? 'disable' : 'enable'} this later.</>}

            />
            <SnackbarNotification
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
            <ProjectClonePopup
                open={projectClonePopupOpen}
                onClose={handleProjectCloneClose}
                onSubmit={handleReviewStagesClone}
                clonePopUpTitle="Review Stages"
            />
        </>
    );
};

export default ReviewList;
